import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RequestCard } from '../components/RequestCard';
import { get2DRRequests, delete2DRRequest } from '../config/firebase';
import type { ExtendedDonationRequest } from '../types';
import { useAuth } from '../hooks/useAuth';
import { canModifyRequest } from '../utils/requestUtils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAudioPlayer } from '../context/AudioPlayerContext';

export function TwoDR() {
  const location = useLocation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ExtendedDonationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { pause, currentTrack } = useAudioPlayer();

  // Séparer les demandes audio et image
  const audioRequests = requests.filter(request => request.audioUrl);
  const imageRequests = requests.filter(request => !request.audioUrl);

  // Fonction pour filtrer les requêtes
  const filterRequests = (fetchedRequests: ExtendedDonationRequest[]) => {
    return fetchedRequests.filter(request => {
      if (!request.isPublic) return user?.id === request.userId;
      
      if (request.hasFixedAmount) {
        const totalDonations = (request.donations || [])
          .reduce((sum, d) => sum + d.amount, 0);
        return totalDonations < request.amount && 
               (request.amount - totalDonations) >= (request.minDonationAmount || 100);
      }
      
      return true;
    });
  };

  // Charger les requêtes une seule fois au montage du composant
  useEffect(() => {
    let isMounted = true;

    const fetchRequests = async () => {
      if (!isMounted) return;
      
      setLoading(true);
      try {
        const fetchedRequests = await get2DRRequests();
        if (!isMounted) return;

        const filteredRequests = filterRequests(fetchedRequests);
        setRequests(filteredRequests);
      } catch (error) {
        console.error('Erreur lors de la récupération des demandes:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRequests();

    // Nettoyage
    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  // Gérer l'ajout d'une nouvelle demande
  useEffect(() => {
    if (location.state?.newRequest) {
      setRequests(prev => {
        if (prev.some(r => r.id === location.state.newRequest.id)) {
          return prev;
        }
        const updatedRequests = [...prev];
        const middleIndex = Math.floor(updatedRequests.length / 2);
        updatedRequests.splice(middleIndex, 0, location.state.newRequest);
        return updatedRequests;
      });
      
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const handleLike = async (requestId: string) => {
    if (!user?.id) return;

    try {
      const requestRef = doc(db, '2dr_requests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (requestDoc.exists()) {
        setRequests(prev => prev.map(request => {
          if (request.id === requestId) {
            return {
              ...request,
              likes: (request.likes || 0) + 1
            };
          }
          return request;
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du like:', error);
    }
  };

  const handleDelete = async (requestId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      // Vérifier si la demande en cours de suppression est celle qui est en train de jouer
      const requestToDelete = requests.find(req => req.id === requestId);
      if (requestToDelete?.audioUrl && currentTrack?.id === requestId) {
        // Arrêter la lecture audio si c'est la demande en cours
        pause();
      }

      // Supprimer la demande
      await delete2DRRequest(requestId);
      
      // Mettre à jour la liste des demandes
      setRequests(prev => prev.filter(req => req.id !== requestId));
      
      alert('Demande supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const navigateToRequestDetails = (requestId: string, requestData?: ExtendedDonationRequest) => {
    if (requestData) {
      const savedRequests = localStorage.getItem('2dr_requests');
      let requests = [];
      
      if (savedRequests) {
        requests = JSON.parse(savedRequests);
        const existingIndex = requests.findIndex((r: any) => r.id === requestId);
        
        if (existingIndex !== -1) {
          requests[existingIndex] = requestData;
        } else {
          requests.push(requestData);
        }
      } else {
        requests = [requestData];
      }
      
      localStorage.setItem('2dr_requests', JSON.stringify(requests));
    }
    
    navigate(`/2dr/request/${requestId}`);
  };

  const handleCardClick = (requestId: string, request: ExtendedDonationRequest) => {
    navigateToRequestDetails(requestId, request);
  };

  if (loading) {
    return <div className="p-4 text-center text-white">Chargement...</div>;
  }

  return (
    <div className="min-h-screen pt-[64px]">
      {/* Section Audio - Fixée sous le header */}
      {audioRequests.length > 0 && (
        <div className="sticky top-[64px] bg-black/90 backdrop-blur-lg z-10 border-b border-white/10">
          {audioRequests.map((request, index) => (
            <div 
              key={`audio-${request.id}-${index}`}
              className="cursor-pointer"
            >
              <RequestCard
                {...request}
                description={request.details}
                collected={request.donations?.reduce((sum, d) => sum + d.amount, 0) || 0}
                goal={request.amount || 0}
                donations={request.donations || []}
                likes={request.likes || 0}
                onLike={() => handleLike(request.id)}
                onDelete={async () => {
                  if (!user?.id) return;
                  const canModify = await canModifyRequest(request.id, user.id);
                  if (canModify) {
                    await handleDelete(request.id);
                  }
                }}
                onEdit={() => {
                  if (!user?.id) return;
                  navigate(`/2dr/edit/${request.id}`);
                }}
                hasFixedAmount={request.hasFixedAmount || false}
                minDonationAmount={request.minDonationAmount || 100}
                userId={request.userId}
                showControls={user?.id === request.userId && !request.donations?.length}
                showAuthPrompt={!user}
                creatorName={request.creatorName || ''}
                creatorCity={request.creatorCity || ''}
                creatorCountry={request.creatorCountry || ''}
                creatorId={request.userId || ''}
              />
            </div>
          ))}
        </div>
      )}

      {/* Section Images */}
      <div className="relative overflow-hidden pt-2">
        {imageRequests.length > 0 ? (
          <div className="container mx-auto px-4 py-2 space-y-3 overflow-y-auto">
            {imageRequests
              .filter(request => request.title !== 'Document initial')
              .map((request, index) => (
                <div 
                  key={`image-${request.id}-${index}`}
                  onClick={() => handleCardClick(request.id, request)}
                  className="cursor-pointer mt-2"
                >
                  <RequestCard
                    {...request}
                    description={request.details}
                    collected={request.donations?.reduce((sum, d) => sum + d.amount, 0) || 0}
                    goal={request.amount || 0}
                    donations={request.donations || []}
                    images={request.images || []}
                    likes={request.likes || 0}
                    onLike={() => handleLike(request.id)}
                    onDelete={async () => {
                      if (!user?.id) return;
                      const canModify = await canModifyRequest(request.id, user.id);
                      if (canModify) {
                        await handleDelete(request.id);
                      }
                    }}
                    onEdit={() => {
                      if (!user?.id) return;
                      navigate(`/2dr/edit/${request.id}`);
                    }}
                    hasFixedAmount={request.hasFixedAmount || false}
                    minDonationAmount={request.minDonationAmount || 100}
                    userId={request.userId}
                    showControls={user?.id === request.userId && !request.donations?.length}
                    showAuthPrompt={!user}
                    creatorName={request.creatorName || ''}
                    creatorCity={request.creatorCity || ''}
                    creatorCountry={request.creatorCountry || ''}
                    creatorId={request.userId || ''}
                  />
                </div>
              ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400">Aucune demande disponible</p>
          </div>
        )}
      </div>

      <button
        onClick={() => navigate('/2dr/create')}
        className="fixed right-4 bottom-20 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-colors flex items-center justify-center z-50"
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}